import { IconExternalLink, TextLink, Typography } from '@up-riac/bespoke-ui';
import dayjs from 'dayjs';
import React, { Suspense, useContext } from 'react';
import { AuctionDate } from 'src/components/AuctionDate/AuctionDate';
import { HeaderMobileMenuHeader } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuHeader';
import { HeaderMobileMenuNavItem } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuNavItem';
import { ActiveAuction } from 'src/lib/GlobalDataContext';

const getAuctionLocation = (auction: {AuctionTypeID: number, LocCity: string, LocState: string}) => {
  return auction.AuctionTypeID == TIMED_AUCTION_TYPE_ID
    ? 'Online'
    : auction.LocCity
      ? `${auction.LocCity}, ${auction.LocState}`
      : '';
};

const HeaderMobileMenuAuctionsPageSubpages = ({ localPage, setLocalPage, closeMenu, upcomingAuctions, pastAuctions }) => {
  return (
    <>
      {localPage === 'upcoming' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-upcoming-header'
          onBack={() => setLocalPage('main')}
          onPress={closeMenu}
          to='/gun-auctions#upcoming'
        >
          Upcoming Auctions
        </HeaderMobileMenuHeader>
        {upcomingAuctions.map((auction, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-upcoming-item-${index}`}
            id={`header-mobile-menu-upcoming-item-${index}`}
            label={auction.Title}
            noArrow
            description={<>
              <AuctionDate
                omitIcon
                auction = {auction}
                omitTime
              /><br/>
              {getAuctionLocation(auction)}
            </>}
            to={auction.CatalogSearchLink ? `/catalog/${auction.AuctionID}` : `/gun-auctions/${auction.Slug}`}
            onPress={() => closeMenu()}
          />
        ))}
      </>}
      {localPage === 'past' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-past-header'
          onBack={() => setLocalPage('main')}
          onPress={closeMenu}
          to='/gun-auctions#past'
        >
          Past Auctions
        </HeaderMobileMenuHeader>
        {pastAuctions.map((auction, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-past-item-${index}`}
            id={`header-mobile-menu-past-item-${index}`}
            label={auction.Title}
            noArrow
            description={<>
              <AuctionDate
                omitIcon
                auction = {auction}
                omitTime
              /><br/>
              {getAuctionLocation(auction)}
            </>}
            to={`/catalog/${auction.AuctionID}`}
            onPress={() => closeMenu()}
          />
        ))}
        <HeaderMobileMenuNavItem
          noArrow
          id='header-mobile-menu-past-view-all-item'
          label='View All Past Auctions'
          to='/gun-auctions#past'
          onPress={() => closeMenu()}
        />
      </>}
    </>
  );
};

export const HeaderMobileMenuAuctionsPage = ({ setPage, closeMenu, currentAuctions, upcomingAuctions, pastAuctions }: {closeMenu: () => void, setPage: React.Dispatch<React.SetStateAction<'main' | 'login' | 'auctions' | 'consignment' | 'latest' | 'company' | 'locations' | 'account' | 'account/profile' | 'account/settings' | 'account/buying'>>, currentAuctions: ActiveAuction[], upcomingAuctions: ActiveAuction[], pastAuctions: ActiveAuction[] }) => {
  const [localPage, setLocalPage] = React.useState<'main' | 'upcoming' | 'past'>('main');

  return (<>
    {localPage === 'main' && <>
      <HeaderMobileMenuHeader
        to='/gun-auctions'
        onPress={closeMenu}
        id='header-mobile-menu-auctions-page-header'
        onBack={() => setPage('main')}
      >
        Auctions
      </HeaderMobileMenuHeader>
      {currentAuctions.length === 0 && <div className={'py-4 pr-[7px] pl-5'}>
        <Typography
          color='text-lightest'
          className='italic'
          size='sm'
        >
          No current auctions scheduled at this time. Be the first to know about our new auctions.
        </Typography>
        <TextLink
          to='https://rockislandauction.us13.list-manage.com/subscribe?u=ade8396c719b3abbeacf57a7e&id=1f4b8db853&ref=rockislandauction.com'
          color='text'
          UNSAFE_className='font-bold text-xs'
        >
          Get notified <IconExternalLink className='size-4 mt-0.5'/>
        </TextLink>
      </div> || currentAuctions.slice(0, 2).map((auction, index) => (
        <HeaderMobileMenuNavItem
          key={`header-mobile-menu-auction-page-upcoming-auction-${index}`}
          id={`header-mobile-menu-auction-page-upcoming-auction-${index}`}
          label={auction.Title}
          noArrow
          accent
          description={
            <AuctionDate
              omitIcon
              auction = {auction}
              omitTime
            />
          }
          to={`/catalog/${auction.AuctionID}`}
          onPress={() => closeMenu()}
        />
      ))}
      <HeaderMobileMenuNavItem onPress={() => setLocalPage('upcoming')} id='header-mobile-menu-auction-page-upcoming' label='Upcoming Auctions'/>
      <HeaderMobileMenuNavItem onPress={() => setLocalPage('past')} id='header-mobile-menu-auction-page-past' label='Past Auctions'/>
    </>}
    <Suspense fallback={<Typography color='text-light'>Loading...</Typography>}>
      <HeaderMobileMenuAuctionsPageSubpages
        upcomingAuctions={upcomingAuctions}
        pastAuctions={pastAuctions}
        closeMenu={closeMenu} localPage={localPage} setLocalPage={setLocalPage} />
    </Suspense>
  </>);
};
